import { useEffect } from 'react';
import { getLibrariesCached } from '../api/abs';
import {
  connectionsStore,
  getActiveConnection,
  requestLibraryIdFor,
  saveConnectionLibraries,
  selectConnectionLibrary,
  toConnectionLibraries,
} from '../api/connections';
import { isAudiobookLibrary, NO_ENABLED_LIBRARIES } from '../api/helpers';
import { getSession, sessionStore } from '../api/session';
import type { Library } from '../api/types';
import { createStore, useStore } from '../lib/store';
import { settingsStore } from '../settings';
import { userScopeKey } from './scope';

// Which library the app shows. Resolution follows Flutter's
// ServerDataService.resolveLibraryId plus the PWA's server default:
// connection selection → saved selection → server default → first audiobook
// library. Libraries are cached for 2 minutes per server + token and saved
// on the connection so the list is known offline.

export type LibrariesStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface ActiveLibraryState {
  /** `abs|<serverUrl>|<userId>` the libraries belong to (null when signed out). */
  scope: string | null;
  /** Every library on the server. */
  libraries: Library[];
  /** Audiobook libraries the user can pick (enabled ones when the connection restricts them). */
  bookLibraries: Library[];
  /** What the active connection asks for: `''`, a library id, or the no-enabled-libraries sentinel. */
  requestedLibraryId: string;
  /** The resolved library (null when none is available). */
  libraryId: string | null;
  library: Library | null;
  /** The connection has libraries but every one is disabled. */
  noEnabledLibraries: boolean;
  status: LibrariesStatus;
  error: unknown;
  /** Epoch ms of the last successful fetch. */
  fetchedAt: number;
}

export interface ResolveLibraryInput {
  libraries: readonly Library[];
  requestedLibraryId: string;
  enabledLibraryIds?: readonly string[];
  savedSelectionId?: string;
  defaultLibraryId?: string;
}

/** Pure resolution (exported for tests). */
export function resolveLibraryId(input: ResolveLibraryInput): string | null {
  const requested = input.requestedLibraryId.trim();
  if (requested === NO_ENABLED_LIBRARIES) return null;
  const known = input.libraries.length > 0;
  const enabled = input.enabledLibraryIds ?? [];
  const candidates = enabled.length > 0 ? input.libraries.filter((l) => enabled.includes(l.id)) : [...input.libraries];
  const has = (id: string | undefined): id is string => !!id && candidates.some((l) => l.id === id);
  if (requested && (!known || has(requested))) return requested;
  if (!known) return input.savedSelectionId || input.defaultLibraryId || null;
  if (has(input.savedSelectionId) && candidates.some((l) => l.id === input.savedSelectionId && isAudiobookLibrary(l))) return input.savedSelectionId;
  if (has(input.defaultLibraryId) && candidates.some((l) => l.id === input.defaultLibraryId && isAudiobookLibrary(l))) return input.defaultLibraryId;
  return candidates.find(isAudiobookLibrary)?.id ?? null;
}

const INITIAL: ActiveLibraryState = {
  scope: null,
  libraries: [],
  bookLibraries: [],
  requestedLibraryId: '',
  libraryId: null,
  library: null,
  noEnabledLibraries: false,
  status: 'idle',
  error: null,
  fetchedAt: 0,
};

export const activeLibraryStore = createStore<ActiveLibraryState>(INITIAL);

function cachedConnectionLibraries(): Library[] {
  return (getActiveConnection()?.libraries ?? []).map((l) => ({ id: l.id, name: l.name || l.id, mediaType: l.mediaType || 'book' }));
}

function derive(prev: ActiveLibraryState, patch: Partial<ActiveLibraryState> = {}): ActiveLibraryState {
  const base = { ...prev, ...patch };
  const session = getSession();
  const connection = getActiveConnection();
  const requestedLibraryId = connection ? requestLibraryIdFor(connection) : '';
  const enabled = connection?.enabledLibraryIds ?? [];
  const libraryId = session
    ? resolveLibraryId({
        libraries: base.libraries,
        requestedLibraryId,
        enabledLibraryIds: enabled,
        savedSelectionId: settingsStore.get().selectedLibraryId,
        defaultLibraryId: session.defaultLibraryId ?? connection?.defaultLibraryId,
      })
    : null;
  const bookLibraries = base.libraries.filter((l) => isAudiobookLibrary(l) && (enabled.length === 0 || enabled.includes(l.id)));
  const next: ActiveLibraryState = {
    ...base,
    requestedLibraryId,
    libraryId,
    library: base.libraries.find((l) => l.id === libraryId) ?? null,
    bookLibraries,
    noEnabledLibraries: requestedLibraryId === NO_ENABLED_LIBRARIES,
  };
  const same =
    next.scope === prev.scope &&
    next.libraries === prev.libraries &&
    next.requestedLibraryId === prev.requestedLibraryId &&
    next.libraryId === prev.libraryId &&
    next.status === prev.status &&
    next.error === prev.error &&
    next.fetchedAt === prev.fetchedAt &&
    next.noEnabledLibraries === prev.noEnabledLibraries &&
    next.bookLibraries.map((l) => l.id).join('|') === prev.bookLibraries.map((l) => l.id).join('|');
  return same ? prev : next;
}

function onDependencies(): void {
  const scope = userScopeKey();
  const prev = activeLibraryStore.get();
  if (scope !== prev.scope) {
    activeLibraryStore.set(derive(INITIAL, { scope, libraries: scope ? cachedConnectionLibraries() : [] }));
    return;
  }
  activeLibraryStore.set(derive(prev));
}

sessionStore.subscribe(onDependencies);
connectionsStore.subscribe(onDependencies);
settingsStore.subscribe(onDependencies);
onDependencies();

let generation = 0;

/**
 * Fetches the library list (2-minute cache unless `force`), saves it on the
 * active connection, and re-resolves the active library. Errors keep the
 * cached list and are reported in `status`/`error`.
 */
export async function loadLibraries(opts: { force?: boolean } = {}): Promise<Library[]> {
  const session = getSession();
  if (!session) return [];
  const scope = userScopeKey(session);
  const gen = ++generation;
  activeLibraryStore.set((s) => derive(s, { status: s.libraries.length > 0 && s.status === 'ready' ? 'ready' : 'loading', error: null }));
  try {
    const libraries = await getLibrariesCached(undefined, { force: opts.force });
    if (gen !== generation || userScopeKey() !== scope) return libraries;
    const connection = getActiveConnection();
    if (connection) saveConnectionLibraries(connection.id, toConnectionLibraries(libraries));
    activeLibraryStore.set((s) => derive(s, { libraries, status: 'ready', error: null, fetchedAt: Date.now() }));
    persistResolvedSelection();
    return libraries;
  } catch (error) {
    if (gen === generation && userScopeKey() === scope) activeLibraryStore.set((s) => derive(s, { status: 'error', error }));
    throw error;
  }
}

/** Remembers the resolved library on the connection so later launches ask for it directly (Flutter persists it too). */
function persistResolvedSelection(): void {
  const { libraryId, requestedLibraryId } = activeLibraryStore.get();
  const connection = getActiveConnection();
  if (connection && libraryId && requestedLibraryId === '' && connection.selectedLibraryId !== libraryId) {
    selectConnectionLibrary(connection.id, libraryId);
  }
}

/** Resolves the active library, loading the library list first when needed (falls back to cached data offline). */
export async function resolveActiveLibraryId(): Promise<string | null> {
  const state = activeLibraryStore.get();
  if (!getSession()) return null;
  if (state.requestedLibraryId === NO_ENABLED_LIBRARIES) return null;
  const fresh = state.status === 'ready' && Date.now() - state.fetchedAt < 2 * 60_000;
  if (!fresh) await loadLibraries().catch(() => undefined);
  return activeLibraryStore.get().libraryId;
}

export function useActiveLibrary(): ActiveLibraryState {
  return useStore(activeLibraryStore);
}

export function useActiveLibraryId(): string | null {
  return useStore(activeLibraryStore, (s) => s.libraryId);
}

/** Active library state; loads the library list on mount (and on account change) when it is missing or stale. */
export function useEnsureActiveLibrary(): ActiveLibraryState {
  const state = useStore(activeLibraryStore);
  useEffect(() => {
    const s = activeLibraryStore.get();
    if (!s.scope || s.status === 'loading') return;
    if (s.status !== 'ready' || Date.now() - s.fetchedAt > 2 * 60_000) void loadLibraries().catch(() => undefined);
  }, [state.scope]);
  return state;
}
